<template>
  <span class="p-chip" :class="`p-chip--${variante}`">
    <IconeSVG
      v-if="config.icone"
      class="p-chip__icone"
      :icone="config.icone"
      cor="currentColor"
      width="14px"
      height="14px"
      aria-hidden="true"
    />
    <span v-else class="p-chip__ponto" aria-hidden="true" />
    <span class="p-chip__texto">
      <slot>{{ texto }}</slot>
    </span>
  </span>
</template>

<script>
import IconeSVG from "@/common/IconeSVG.vue";

/**
 * Chip de status (cor + forma + texto).
 *
 * Status NUNCA é comunicado só por cor: cada variante combina uma cor
 * semântica, uma FORMA distinta (ícone/ponto, legível em daltonismo e P&B)
 * e o texto real do rótulo (sempre lido por leitor de tela).
 */
const VARIANTES = {
  sucesso: { icone: "certo" },
  alerta: { icone: "alerta" },
  erro: { icone: "errado" },
  info: { icone: "informativo" },
  neutro: { icone: null }, // forma = ponto (renderizado via CSS)
};

export default {
  name: "PChipDeStatus",

  components: { IconeSVG },

  props: {
    variante: {
      type: String,
      default: "neutro",
      validator: (valor) => Object.keys(VARIANTES).includes(valor),
    },
    texto: {
      type: String,
      default: "",
    },
    /**
     * Ícone SVG opcional que sobrescreve o padrão da variante.
     * Use quando o estado semântico requer um ícone diferente do default
     * da variante (ex.: alerta com ícone de justificativa pendente).
     * null = usa o ícone padrão da variante.
     */
    icone: {
      type: String,
      default: null,
    },
  },

  computed: {
    config() {
      const base = VARIANTES[this.variante] || VARIANTES.neutro;
      if (this.icone !== null) {
        return { icone: this.icone };
      }
      return base;
    },
  },
};
</script>

<style scoped lang="scss">
/*
 * Harmonização com o Design System (doc 16 §4 · Lote 5): o chip casa com o
 * `PontotelTagDeTexto` do DS — pílula de CONTORNO (fundo neutro, conteúdo
 * preto, BORDA na cor semântica da variante). Consome só tokens do DS
 * (`--tag-*`, `--texto-*` de `@Lar21/design-tokens`), logo acompanha o
 * white-label Sankhya automaticamente (sankhya.css re-declara `--tag-*`).
 *
 * Fundação §3.2 preservada (cor + forma + texto, nunca cor sozinha): a cor
 * semântica vem da BORDA; a FORMA do ícone/ponto (sempre visível, em preto,
 * legível em daltonismo/P&B) e o TEXTO do rótulo continuam no DOM.
 * Tamanho = token PP do DS (compacto p/ linha de tabela de 28–32px, §3.3;
 * altura efetiva ≈ 22px: borda 2+2 + padding 2+2 + entrelinha 14).
 */
.p-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--tag-espacamento-pp-gap);
  max-width: 100%;
  padding: var(--tag-espacamento-pp-vertical) var(--tag-espacamento-pp-horizontal);
  border: 1px solid var(--cor-chip);
  border-radius: var(--tag-arredondamento-borda);
  background-color: var(--tag-cor-background);
  color: var(--tag-cor-conteudo); /* conteúdo/ícone (currentColor) em preto, como o DS */
  font-family: var(--texto-tipografia);
  font-size: var(--texto-paragrafo-pp-tamanho);
  font-weight: 600;
  line-height: var(--texto-paragrafo-pp-entrelinha);
  white-space: nowrap;
  vertical-align: middle;
  box-sizing: border-box;
}

.p-chip__icone {
  flex: 0 0 auto;
}

.p-chip__ponto {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: var(--tag-arredondamento-borda);
  background-color: currentColor;
}

.p-chip__texto {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Cor semântica por variante → borda (mapeada 1:1 ao PontotelTagDeTexto do DS) */
.p-chip--sucesso {
  --cor-chip: var(--tag-cor-sucesso-borda);
}

.p-chip--alerta {
  --cor-chip: var(--tag-cor-alerta-borda);
}

.p-chip--erro {
  --cor-chip: var(--tag-cor-erro-borda);
}

.p-chip--info {
  --cor-chip: var(--tag-cor-info-borda);
}

.p-chip--neutro {
  --cor-chip: var(--tag-cor-neutra-borda);
}
</style>
